import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from "v-select2-multiple-component";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ExcelImportPreviewMixin from "@/mixins/excel-import-preview";
import ExcelImportConfirmErrorsMixin from "@/mixins/excel-import-confirm-errors";
import ValidationService from "@/services/ValidationService";

const maxSize5MB = (value) => !value || value.size <= 5 * 1024 * 1024;

export default {
  name: "SubjectTeacherImport",
  components: {
    MainLayout,
    Select2Multiple,
    Loading,
  },

  mixins: [ValidationService, ExcelImportPreviewMixin, ExcelImportConfirmErrorsMixin],

  data() {
    return {
      isLoading: false,
      disableImportBtn: false,
      disableConfirmBtn: false,
      disableInputFields: false,
      disableAcademicYear: false,
      submitted: false,
      templateSubmitted: false,
      importStep: "form",
      periodArr: [],
      template_academic_year_id: null,
      academic_year_id: null,
      file_name: "",
      selectedFile: null,
      invalidFileType: false,
      previewRows: [],
      previewTotal: 0,
    };
  },

  validations: {
    academic_year_id: { required },
    selectedFile: { required, maxSize5MB },
  },

  computed: {
    disabledTemplateBtn() {
      return this.disableInputFields;
    },

    selectedAcademicYearName() {
      const selected = this.periodArr.find(
        (period) => String(period.id) === String(this.academic_year_id)
      );

      return selected?.text || this.previewRows[0]?.academic_year_name || "";
    },

    isPreviewStep() {
      return this.importStep === "preview";
    },
  },

  watch: {
    academic_year_id() {
      if (this.isPreviewStep) {
        this.resetPreviewState();
      }
    },
  },

  methods: {
    getAcademicYear() {
      this.isLoading = true;
      axios.get("period/periodList")
        .then((res) => {
          this.periodArr = res.data.data || [];
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    chooseFile() {
      if (this.isPreviewStep) {
        return;
      }
      this.$refs.fileUploader.value = null;
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.$refs.fileUploader.click();
    },

    onFileChange(event) {
      const file = event.target.files[0];
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;

      if (this.isPreviewStep) {
        this.resetPreviewState();
      }

      if (!file) {
        return;
      }

      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!["xls", "xlsx"].includes(extension)) {
        this.invalidFileType = true;
        return;
      }

      this.selectedFile = file;
      this.file_name = file.name;
    },

    getTemplateFileName() {
      const selected = this.periodArr.find(
        (period) => String(period.id) === String(this.template_academic_year_id)
      );
      const periodName = (selected?.text || "all").replace(/\s+/g, "_");
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      return `${periodName}_subject_assign_${year}_${month}_${day}.xlsx`;
    },

    downloadTemplate() {
      this.templateSubmitted = true;

      if (this.disabledTemplateBtn) {
        return;
      }

      const params = {};
      if (this.template_academic_year_id) {
        params.academic_year_id = this.template_academic_year_id;
      }

      this.isLoading = true;
      axios.get("subjectTeacher/excelTemplateDownload", {
        params,
        responseType: "blob",
      })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const link = document.createElement("a");

          link.href = window.URL.createObjectURL(blob);
          link.download = this.getTemplateFileName();
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmImport() {
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid || this.invalidFileType) {
        return;
      }

      this.validateImport();
    },

    validateImport() {
      this.isLoading = true;
      this.disableImportBtn = true;
      this.disableInputFields = true;
      this.disableAcademicYear = true;

      const formData = new FormData();
      formData.append("academic_year_id", this.academic_year_id);
      formData.append("file", this.selectedFile);

      axios.post("subjectTeacher/excelImportValidate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((response) => {
          this.previewRows = this.clonePreviewRows(response.data.preview || []);
          this.previewTotal = response.data.total || this.previewRows.length;
          this.clearImportConfirmErrors();
          this.importStep = "preview";
        })
        .catch((error) => {
          this.handleImportValidationError(error);
          this.disableInputFields = false;
          this.disableImportBtn = false;
          this.disableAcademicYear = false;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmImportSubmit() {
      this.clearImportConfirmErrors();

      if (!this.previewRows.length) {
        this.$notification.error("No preview data to import.", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      const fieldErrors = this.validatePreviewBeforeConfirm();
      if (Object.keys(fieldErrors).length) {
        this.setConfirmFieldErrors(fieldErrors);
        return;
      }

      this.isLoading = true;
      this.disableConfirmBtn = true;

      axios.post("subjectTeacher/excelImportConfirm", {
        academic_year_id: this.academic_year_id,
        preview: this.buildConfirmPreviewPayload(),
      })
        .then((response) => {
          this.$notification.success(response.data.message || "Subject assign imported successfully.", {
            timer: 3,
            position: "bottomCenter",
          });
          this.$router.push({ name: "subjectTeacherList" });
        })
        .catch((error) => {
          this.handleConfirmBackendError(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.disableConfirmBtn = false;
        });
    },

    clonePreviewRows(rows) {
      return (rows || []).map((row) => ({
        academic_year_name: row.academic_year_name || "",
        class_name: String(row.class_name || "").trim(),
        teacher_name: String(row.teacher_name || "").trim(),
        teacher_no: String(row.teacher_no || "").trim(),
        subject_name: String(row.subject_name || "").trim(),
        class_id: row.class_id,
        teacher_id: row.teacher_id,
        subject_id: row.subject_id,
      }));
    },

    buildConfirmPreviewPayload() {
      return this.previewRows.map((row, index) => ({
        row_no: index + 1,
        class_name: String(row.class_name || "").trim(),
        teacher_name: String(row.teacher_name || "").trim(),
        teacher_no: String(row.teacher_no || "").trim(),
        subject_name: String(row.subject_name || "").trim(),
        class_id: row.class_id,
        teacher_id: row.teacher_id,
        subject_id: row.subject_id,
      }));
    },

    normalizeImportKey(value) {
      return String(value || "").replace(/\s+/g, " ").trim().toLowerCase();
    },

    validatePreviewBeforeConfirm() {
      const errors = {};
      const seenCombos = {};

      this.previewRows.forEach((row, index) => {
        const rowErrors = {};
        const className = String(row.class_name || "").trim();
        const teacherName = String(row.teacher_name || "").trim();
        const teacherNo = String(row.teacher_no || "").trim();
        const subjectName = String(row.subject_name || "").trim();

        if (!className) {
          rowErrors.class_name = "Class Name is required.";
        } else if (className.length > 255) {
          rowErrors.class_name = "Max 255 characters.";
        }

        if (!teacherName) {
          rowErrors.teacher_name = "Teacher Name is required.";
        } else if (teacherName.length > 255) {
          rowErrors.teacher_name = "Max 255 characters.";
        }

        if (!teacherNo) {
          rowErrors.teacher_no = "Teacher No is required.";
        } else if (teacherNo.length > 255) {
          rowErrors.teacher_no = "Max 255 characters.";
        }

        if (!subjectName) {
          rowErrors.subject_name = "Subject Name is required.";
        } else if (subjectName.length > 255) {
          rowErrors.subject_name = "Max 255 characters.";
        }

        if (className && teacherNo && subjectName) {
          const comboKey = [
            this.normalizeImportKey(className),
            this.normalizeImportKey(teacherNo),
            this.normalizeImportKey(subjectName),
          ].join("|");

          if (seenCombos[comboKey]) {
            rowErrors.class_name = "Duplicate assignment.";
          } else {
            seenCombos[comboKey] = true;
          }
        }

        if (Object.keys(rowErrors).length) {
          errors[index] = rowErrors;
        }
      });

      return errors;
    },

    handleImportValidationError(error) {
      if (error.response?.status === 422) {
        const data = error.response.data || {};
        const messages = data.errors?.length
          ? data.errors
          : [data.msg || data.message || "Import validation failed."];

        window.alert(messages.join("\n"));
        return;
      }

      this.handleError(error);
    },

    handleConfirmBackendError(error) {
      if (this.applyConfirmBackendErrors(error, this.getImportConfirmErrorOptions())) {
        return;
      }
      this.handleError(error);
    },

    getImportConfirmErrorOptions() {
      return { rowKey: "row_no" };
    },

    resetPreviewState() {
      this.importStep = "form";
      this.disableInputFields = false;
      this.disableAcademicYear = false;
      this.disableImportBtn = false;
      this.disableConfirmBtn = false;
      this.previewRows = [];
      this.previewTotal = 0;
      this.clearImportConfirmErrors();
    },

    resetForm() {
      this.submitted = false;
      this.templateSubmitted = false;
      this.resetPreviewState();
      this.template_academic_year_id = null;
      this.academic_year_id = null;
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      this.$v.$reset();
      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }
      this.getAcademicYear();
    },

    handleError(error) {
      if (error.response?.status === 401) {
        this.$store.commit("logout");
        window.location.replace("login");
        return;
      }

      const message = error.response?.data?.message || "Something went wrong.";
      this.$notification.error(message, {
        timer: 3,
        position: "bottomCenter",
      });
    },

    clearFileInput() {
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }
    },
  },

  mounted() {
    this.getAcademicYear();
  },
};
